'use client'

import { useEffect, useRef } from 'react'

import { useCachedSWR } from '@/core/lib/swr/use-cached-swr'
import { useAppReadScope } from '@/features/veluvit/hooks/use-app-read-scope'
import { appSwrKey } from '@/features/veluvit/lib/app-read'
import {
  HOME_CATALOG_PATH,
  projectHome,
} from '@/features/veluvit/lib/home-read'
import { homeCatalogSchema } from '@/features/veluvit/schemas/home.schema'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { z } from 'zod'

export function useHome() {
  const epoch = useAppReadScope()
  const hidden = useSessionStore((state) => state.data?.hidden)
  const blocked = useSessionStore(
    (state) => state.data?.user?.settings?.blockedCreators
  )
  const preferences = JSON.stringify([hidden ?? [], blocked ?? []])
  const previous = useRef<{ epoch: string | null; preferences: string } | null>(
    null
  )
  const { data, error, mutate } = useCachedSWR<
    z.infer<typeof homeCatalogSchema>
  >(epoch ? appSwrKey(epoch, HOME_CATALOG_PATH) : null, 'REALTIME', {
    responseSchema: homeCatalogSchema,
  })

  useEffect(() => {
    const before = previous.current
    previous.current = { epoch, preferences }
    if (
      epoch &&
      before?.epoch === epoch &&
      before.preferences !== preferences
    ) {
      void mutate()
    }
  }, [epoch, preferences, mutate])

  return {
    data: data?.ok ? projectHome(data.data) : null,
    error: error?.message ?? (data && !data.ok ? data.message : null),
    reload: () => {
      void mutate()
    },
  }
}
